import { useCanWrite } from '@hooks/useCanWrite';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import {
  ArrowDownLeft,
  ClipboardList,
  Paperclip,
  Plus,
  RefreshCw,
  Send,
  X,
} from 'lucide-react';
import { Button } from '@components/atoms/Button';
import { Tab } from '@components/atoms/Tab';
import { BidStatusBadge } from '@components/atoms/BidStatusBadge';
import { EmptyData } from '@components/molecules/EmptyData';
import { SearchInput } from '@components/molecules/SearchInput';
import { DataTable } from '@components/molecules/DataTable';
import type { DataTableColumn } from '@components/molecules/DataTable';
import { TablePagination } from '@components/molecules/TablePagination';
import BudgetDocumentsModal from '@components/organisms/BudgetDocumentsModal';
import {
  useBudget,
  usePaginatedBudgetRequests,
  useBudgetRequestDetails,
} from '@hooks/api/useBudget';
import { useDebounce } from '@hooks/useDebounce';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import {
  BudgetPerspective,
  BudgetRequest,
  BudgetRowStatus,
  BUDGET_ROW_STATUS_LABEL,
  BUDGET_ROW_STATUS_ORDER,
} from '@models/budget.model';
import { Document } from '@models/document.model';
import CreateBudgetModal from '@components/CreateBudgetModal';
import BudgetResponseModal from '@components/organisms/BudgetResponseModal';
import BudgetDetailsModal from './components/BudgetDetailsModal';
import { cn } from '@utils/cn';

const DEFAULT_PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 300;

const formatRelativeTime = (dateInput?: Date | string): string => {
  if (!dateInput) return '—';
  const elapsedHours = Math.floor(
    (Date.now() - new Date(dateInput).getTime()) / (1000 * 60 * 60),
  );
  if (elapsedHours < 1) return 'Just now';
  if (elapsedHours < 24) return `${elapsedHours}h ago`;
  const elapsedDays = Math.floor(elapsedHours / 24);
  if (elapsedDays < 7) return `${elapsedDays}d ago`;
  const elapsedWeeks = Math.floor(elapsedDays / 7);
  return elapsedWeeks < 5
    ? `${elapsedWeeks}w ago`
    : `${Math.floor(elapsedDays / 30)}mo ago`;
};

const formatCurrency = (amount?: number): string =>
  amount || amount === 0
    ? new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount)
    : '—';

/** Statuses that mean the row is waiting on the viewer — these get the accent rail. */
const ACTIONABLE_STATUSES: BudgetRowStatus[] = ['NEW', 'COUNTERED'];

const BudgetsPage: React.FC = () => {
  const location = useLocation();
  const history = useHistory();

  const [perspective, setPerspective] = useState<BudgetPerspective>('SENT');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<BudgetRowStatus[]>([]);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [responseModalOpen, setResponseModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [documentsFor, setDocumentsFor] = useState<BudgetRequest | null>(null);
  const [selectedBudgetRequest, setSelectedBudgetRequest] =
    useState<BudgetRequest | null>(null);
  const [urlBudgetRequestId, setUrlBudgetRequestId] = useState<string | undefined>();

  const debouncedSearch = useDebounce(searchTerm, SEARCH_DEBOUNCE_MS);

  const { organizationId } = useAppSelector(
    (state: RootState) => state.organization,
  );
  // Covers both causes of a locked account: a read-only membership, and an organization
  // whose trial has expired.
  const { canWrite } = useCanWrite();
  const isReadOnly = !canWrite;

  const { data: urlBudgetRequestData } = useBudgetRequestDetails(urlBudgetRequestId);

  // One query for the whole surface. Rows, the total, and every filter count come
  // back together — the six extra count queries this page used to fire are gone.
  const { data, isLoading, isFetching, error, refetch } = usePaginatedBudgetRequests({
    page,
    limit: pageSize,
    perspective,
    statusFilter: 'ALL',
    statuses: selectedStatuses,
    search: debouncedSearch,
    enabled: !!organizationId,
  });

  // The opposite tab's badge. Cheapest possible ask: one row, read the count off it.
  const { data: otherPerspectiveData } = usePaginatedBudgetRequests({
    page: 0,
    limit: 1,
    perspective: perspective === 'SENT' ? 'RECEIVED' : 'SENT',
    statusFilter: 'ALL',
    enabled: !!organizationId,
  });

  const {
    createBudgetRequest,
    respondToBudgetRequest,
    acceptBudgetRequest,
    rejectBudgetRequest,
    loading: mutationLoading,
  } = useBudget();

  const budgetRequests = useMemo(() => data?.budgetRequests ?? [], [data]);
  const totalCount = data?.count ?? 0;
  const facets = useMemo(() => data?.facets ?? [], [data]);
  const actionableCount = data?.actionableCount ?? 0;
  const otherActionableCount = otherPerspectiveData?.actionableCount ?? 0;

  const hasPermissionError =
    (error as any)?.response?.errors?.[0]?.extensions?.code === 'FORBIDDEN';
  // Anything that is not a permissions problem still has to be told apart from
  // "you have no bids" — an empty table plus a Create button is a lie when the
  // request failed.
  const hasLoadError = Boolean(error) && !hasPermissionError;

  useEffect(() => {
    if (error) {
      console.warn('Budget requests error:', error);
    }
  }, [error]);

  const countByStatus = useMemo(() => {
    const map = new Map<BudgetRowStatus, number>();
    facets.forEach(({ status, count }) => map.set(status, count));
    return map;
  }, [facets]);

  // Only offer filters that would actually match something, plus whatever is
  // already selected — a chip must never vanish out from under a click.
  const availableStatuses = useMemo(
    () =>
      BUDGET_ROW_STATUS_ORDER.filter(
        (status) => countByStatus.has(status) || selectedStatuses.includes(status),
      ),
    [countByStatus, selectedStatuses],
  );

  const hasActiveFilters = selectedStatuses.length > 0 || debouncedSearch.length > 0;

  // Any change to what is being asked for sends us back to the first page,
  // otherwise page 4 of a narrower result set renders empty.
  useEffect(() => {
    setPage(0);
  }, [perspective, debouncedSearch, selectedStatuses, pageSize]);

  // The row count can shrink underneath us — a bid gets accepted, someone else
  // cancels one — leaving `page` past the end. Without this the table renders
  // empty while the footer reads "Showing 31–12 of 12", recoverable only by
  // clicking Previous.
  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(totalCount / pageSize) - 1);
    if (page > lastPage) {
      setPage(lastPage);
    }
  }, [totalCount, pageSize, page]);

  const openBudgetRequest = useCallback(
    (request: BudgetRequest) => {
      setSelectedBudgetRequest(request);
      const isRecipient = request.recipients?.some(
        (recipient) => recipient.recipientOrganizationId === organizationId,
      );
      const hasProposals = (request.proposals?.length ?? 0) > 0;
      // A recipient with nothing to read yet goes straight to the reply form;
      // everyone else gets the full history first.
      if (isRecipient && request.status === 'ACTIVE' && !hasProposals) {
        setResponseModalOpen(true);
      } else {
        setDetailsModalOpen(true);
      }
    },
    [organizationId],
  );

  // Deep links (?budgetRequestId=…) open a bid directly, even one not on this page.
  useEffect(() => {
    const budgetRequestId = new URLSearchParams(location.search).get('budgetRequestId');
    if (!budgetRequestId) return;

    const onThisPage = budgetRequests.find(
      (request) => request.budgetRequestId === budgetRequestId,
    );

    if (onThisPage) {
      openBudgetRequest(onThisPage);
      history.replace('/budgets');
      setUrlBudgetRequestId(undefined);
    } else {
      setUrlBudgetRequestId(budgetRequestId);
    }
  }, [location.search, budgetRequests, history, openBudgetRequest]);

  useEffect(() => {
    if (urlBudgetRequestData && urlBudgetRequestId) {
      openBudgetRequest(urlBudgetRequestData);
      setUrlBudgetRequestId(undefined);
      history.replace('/budgets');
    }
  }, [urlBudgetRequestData, urlBudgetRequestId, history, openBudgetRequest]);

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleCreateBudgetRequest = async (input: any) => {
    // Sprint 23, story-111 (s4fxo8Ga). createBudgetRequest now resolves falsy on failure instead
    // of rejecting — gate the refresh on success like every sibling handler below already does,
    // rather than running it unconditionally after a failed create.
    if (await createBudgetRequest(input)) {
      handleRefresh();
    }
  };

  const handleRespondToBudgetRequest = async (input: any) => {
    if (await respondToBudgetRequest(input)) {
      setResponseModalOpen(false);
      handleRefresh();
    }
  };

  const handleAcceptBudgetRequest = async () => {
    if (!selectedBudgetRequest) return;
    if (await acceptBudgetRequest(selectedBudgetRequest.budgetRequestId)) {
      setResponseModalOpen(false);
      setSelectedBudgetRequest(null);
      handleRefresh();
    }
  };

  const handleRejectBudgetRequest = async () => {
    if (!selectedBudgetRequest) return;
    if (await rejectBudgetRequest(selectedBudgetRequest.budgetRequestId)) {
      setResponseModalOpen(false);
      setSelectedBudgetRequest(null);
      handleRefresh();
    }
  };

  const toggleStatus = (status: BudgetRowStatus) =>
    setSelectedStatuses((current) =>
      current.includes(status)
        ? current.filter((item) => item !== status)
        : [...current, status],
    );

  const clearFilters = () => {
    setSelectedStatuses([]);
    setSearchTerm('');
  };

  const columns: DataTableColumn<BudgetRequest>[] = useMemo(
    () => [
      {
        key: 'task',
        header: 'Task & project',
        render: (request) => (
          <div className="min-w-0">
            <p className="truncate font-semibold text-gray-900 dark:text-gray-100">
              {request.task?.name || 'Bid request'}
            </p>
            <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">
              {request.task?.project?.name || 'No project'}
            </p>
          </div>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        width: '150px',
        render: (request) =>
          request.rowStatus ? <BidStatusBadge status={request.rowStatus} /> : '—',
      },
      {
        key: 'counterparty',
        header: perspective === 'SENT' ? 'Recipients' : 'From',
        width: '200px',
        hideOnSmall: true,
        render: (request) => {
          if (perspective === 'RECEIVED') {
            return (
              <span className="truncate">
                {request.originatorOrganization?.name || '—'}
              </span>
            );
          }
          const recipients = request.recipients ?? [];
          if (recipients.length === 0) return <span className="text-gray-400">—</span>;
          if (recipients.length === 1) {
            return (
              <span className="truncate">
                {recipients[0].recipientOrganization?.name || '1 firm'}
              </span>
            );
          }
          return <span className="text-gray-600">{recipients.length} firms</span>;
        },
      },
      {
        key: 'amount',
        header: 'Max budget',
        align: 'right',
        width: '130px',
        cellClassName: 'tabular-nums font-medium',
        render: (request) => formatCurrency(request.maxBudgetAmount),
      },
      {
        key: 'documents',
        header: 'Files',
        align: 'center',
        width: '80px',
        render: (request) => {
          const count = request.documents?.length ?? 0;
          if (count === 0) return <span className="text-gray-300">—</span>;
          return (
            <button
              type="button"
              // The row itself opens the bid; this must not do both.
              onClick={(event) => {
                event.stopPropagation();
                setDocumentsFor(request);
              }}
              aria-label={`View ${count} attachment${count === 1 ? '' : 's'}`}
              className={cn(
                'inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium',
                'text-gray-600 hover:bg-indigo-50 hover:text-indigo-700',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
                'dark:text-gray-300 dark:hover:bg-indigo-900/30',
              )}>
              <Paperclip size={14} aria-hidden />
              {count}
            </button>
          );
        },
      },
      {
        key: 'createdAt',
        header: perspective === 'SENT' ? 'Sent' : 'Received',
        align: 'right',
        width: '110px',
        cellClassName: 'text-gray-500 whitespace-nowrap',
        render: (request) => formatRelativeTime(request.createdAt),
      },
    ],
    [perspective],
  );

  const emptyState = hasActiveFilters ? (
    <EmptyData
      description="No bids match these filters."
      textButton="Clear filters"
      onClickButton={clearFilters}
    />
  ) : perspective === 'SENT' ? (
    <EmptyData
      description="No bid requests sent yet"
      textButton="Create bid request"
      iconButton={<Plus size={16} />}
      onClickButton={() => setCreateModalOpen(true)}
      disabled={isReadOnly}
    />
  ) : (
    <EmptyData description="No bid requests received." />
  );

  return (
    // No height cap and no inner scroll: the app shell's <main> already scrolls,
    // and nesting a second scroll region there hides the pagination below the fold.
    <div className="flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ClipboardList className="text-orange-400" size={26} aria-hidden />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Bids</h1>
        </div>

        <div className="flex gap-2">
          <Button
            leftIcon={<Plus size={16} />}
            variant="primary"
            onClick={() => setCreateModalOpen(true)}
            disabled={isReadOnly}>
            Create Bid Request
          </Button>
          <Button variant="tertiary" onClick={handleRefresh} aria-label="Refresh bids">
            <RefreshCw size={16} className={cn(isFetching && 'animate-spin')} />
          </Button>
        </div>
      </div>

      <Tab
        tabs={[
          {
            id: 'SENT',
            name: 'Sent Requests',
            leftIcon: <Send size={16} />,
            count:
              perspective === 'SENT'
                ? actionableCount || undefined
                : otherActionableCount || undefined,
          },
          {
            id: 'RECEIVED',
            name: 'Received Requests',
            leftIcon: <ArrowDownLeft size={16} />,
            count:
              perspective === 'RECEIVED'
                ? actionableCount || undefined
                : otherActionableCount || undefined,
          },
        ]}
        activeTab={perspective}
        onTabChange={(tabId) => setPerspective(tabId as BudgetPerspective)}
        variant="underline"
      />

      {hasPermissionError ? (
        <div className="flex flex-col items-center py-8">
          <h2 className="mb-2 text-lg text-gray-600">Access Restricted</h2>
          <p className="text-center text-gray-600">
            You don&apos;t have permission to view bid requests. Please contact your
            administrator for access.
          </p>
        </div>
      ) : hasLoadError ? (
        <div className="flex flex-col items-center gap-3 py-8">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
            We couldn&apos;t load your bids
          </h2>
          <p className="max-w-md text-center text-gray-600 dark:text-gray-400">
            The request failed. Your bids are still there — this is a problem reaching the
            server.
          </p>
          <Button variant="primary" onClick={handleRefresh} leftIcon={<RefreshCw size={16} />}>
            Try again
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {/* Filter bar. The border matters: on a white page a gray-50 fill alone
              is invisible, so the search field and chips read as loose elements
              floating above the table rather than one control group. */}
          <div className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3.5 dark:border-gray-700 dark:bg-gray-800/50">
            <div className="flex flex-wrap items-center gap-2">
              <SearchInput
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder="Search by task or project…"
                className="w-full sm:w-80"
                inputClassName="bg-white dark:bg-gray-900"
              />
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="ml-auto inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-700">
                  <X size={14} aria-hidden />
                  Clear filters
                </button>
              )}
            </div>

            {availableStatuses.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5">
                {availableStatuses.map((status) => {
                  const isSelected = selectedStatuses.includes(status);
                  const count = countByStatus.get(status) ?? 0;
                  return (
                    <button
                      key={status}
                      type="button"
                      aria-pressed={isSelected}
                      onClick={() => toggleStatus(status)}
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                        'focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
                        isSelected
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200'
                          : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300',
                      )}>
                      {BUDGET_ROW_STATUS_LABEL[status]}
                      <span
                        className={cn(
                          'tabular-nums',
                          isSelected ? 'text-indigo-500' : 'text-gray-400',
                        )}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Table */}
          <div>
            <DataTable<BudgetRequest>
              caption="Bid requests"
              columns={columns}
              rows={budgetRequests}
              rowKey={(request) => request.budgetRequestId}
              onRowClick={openBudgetRequest}
              isRowHighlighted={(request) =>
                Boolean(
                  request.rowStatus && ACTIONABLE_STATUSES.includes(request.rowStatus),
                )
              }
              isLoading={isLoading}
              loadingRowCount={8}
              emptyState={emptyState}
            />
          </div>

          {totalCount > 0 && (
            <TablePagination
              page={page}
              pageSize={pageSize}
              totalCount={totalCount}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          )}
        </div>
      )}

      {/* Modals */}
      <CreateBudgetModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSubmit={handleCreateBudgetRequest}
        isSubmitting={isLoading}
      />

      <BudgetResponseModal
        open={responseModalOpen}
        onClose={() => {
          setResponseModalOpen(false);
          setSelectedBudgetRequest(null);
        }}
        onSubmit={handleRespondToBudgetRequest}
        onAccept={handleAcceptBudgetRequest}
        onReject={handleRejectBudgetRequest}
        budgetRequest={selectedBudgetRequest}
        isSubmitting={mutationLoading}
      />

      {detailsModalOpen && selectedBudgetRequest && (
        <BudgetDetailsModal
          open={detailsModalOpen}
          onClose={() => {
            setDetailsModalOpen(false);
            setSelectedBudgetRequest(null);
          }}
          budgetRequest={selectedBudgetRequest}
          onRefresh={handleRefresh}
        />
      )}

      <BudgetDocumentsModal
        isOpen={Boolean(documentsFor)}
        onClose={() => setDocumentsFor(null)}
        documents={(documentsFor?.documents as Document[]) ?? []}
        bidName={documentsFor?.task?.name}
      />
    </div>
  );
};

export default BudgetsPage;
